import { gsap, Power3, Power2 } from "gsap";
import BezierEasing from 'bezier-easing';

import { ControllerPage } from '../_app/cuchillo/pages/ControllerPage';
import Default from './Default';
import Header from '../layout/Header';
import { Scroll } from '../_app/cuchillo/scroll/Scroll';
import NoScroll from './NoScroll';
import InfiniteScroll from '../_app/cuchillo/scroll/InfiniteScroll';
import InfiniteScroll__Item from '../_app/cuchillo/scroll/InfiniteScroll__Item';
import { GetBy } from '../_app/cuchillo/core/Element';
import { Maths } from "../_app/cuchillo/utils/Maths";
import { Basics, isMobile } from "../_app/cuchillo/core/Basics";

export default class Projects extends NoScroll {
  sliderDom;
  scroller;
  scrollMachine;
  left;
  center;
  right;

  constructor() {
    super();

    this.sliderDom = GetBy.class('__slider', this.container)[0];

    this.scroller = new InfiniteScroll(InfiniteScroll.AXIS_Y, {
      domResize: this.sliderDom,
      container: this.sliderDom,
      multiplicator: 1.3,
      itemClass: InfiniteScroll__Item,
      // minSpeed: 4,
      inverted: false,
      gap: 0,
      hasVirtuaScroll: true
    });

    this.left = new Viewer(GetBy.selector('.__viewer .viewer', this.container)[0]);
    this.center = new Viewer(GetBy.selector('.__viewer .viewer', this.container)[1], 'lg');
    this.right = new Viewer(GetBy.selector('.__viewer .viewer', this.container)[2]);

    this.left.addSiblingViewers(this.right);
    this.left.addSiblingViewers(this.center);
    this.center.addSiblingViewers(this.right);
    this.center.addSiblingViewers(this.left);
    this.right.addSiblingViewers(this.center);
    this.right.addSiblingViewers(this.left);

    this.scrollMachine = new ScrollMachine(GetBy.class('__progress', this.container)[0]);
    this.names = new Names(GetBy.class('__title', this.container)[0]);
  }

  afterShow() {
    super.afterShow();

    this.scroller.show();

    this.left.show(.2);
    this.center.show();
    this.right.show(.4);

    this.scrollMachine.show();
    this.names.show();

    this.scroller.start();
  }

  beforeHide() {
    super.beforeHide();
    this.scroller.stop();

    this.left.hide();
    this.center.hide();
    this.right.hide();

    this.scrollMachine.hide();
    this.names.hide();
  }

  loop() {
    super.loop();

    this.scroller.loop();

    this.scrollMachine.loop(this.scroller.actual);
    this.names.loop(this.scroller.actual);
    this.left.loop(this.scroller.actual);
    this.center.loop(this.scroller.actual);
    this.right.loop(this.scroller.actual);
  }

  dispose() {
    this.scroller.dispose();
    this.scrollMachine.dispose();

    this.center.dispose();
    this.left.dispose();
    this.right.dispose();

    super.dispose();
  }

  resize(__w, __h) {
    super.resize(__w, __h);
    this.scroller.resize();
    this.names.resize();
  }
}

ControllerPage._addPage('projects', Projects);

class Names {
  dom;
  clientsWrapper;
  namesWrapper;
  clients;
  names;
  current = -1;
  sizes = {
    clients: 0,
    names: 0
  }

  constructor(__d) {
    this.dom = __d;
    this.clientsWrapper = GetBy.class('__client', this.dom)[0];
    this.namesWrapper = GetBy.class('__name', this.dom)[0];
    this.clients = GetBy.selector('div', this.clientsWrapper);
    gsap.set(this.clients, { y: '110%' });
    this.names = GetBy.selector('div', this.namesWrapper);
    gsap.set(this.names, { y: '110%' });
  }

  show() {
    this.next(0);
  }

  hide() {
    if (this.current > -1) {
      const oldClient = this.clients[this.current];
      const oldName = this.names[this.current];
      gsap.to(oldClient, { y: '-100%', duration: .3 });
      gsap.to(oldName, { y: '-100%', duration: .3 });
    }
  }

  loop(c) {
    if (this.current !== c) {
      this.next(c);
    }
  }

  next(next) {
    if (this.current > -1) {
      const oldClient = this.clients[this.current];
      const oldName = this.names[this.current];
      gsap.to(oldClient, { y: '-100%', duration: .3 });
      gsap.to(oldName, { y: '-100%', duration: .3 });
    }

    const newClient = this.clients[next];
    const newName = this.names[next];
    gsap.set(newClient, { y: '100%' });
    gsap.set(newName, { y: '100%' });

    const { width: clientX } = newClient.getBoundingClientRect();
    const { width: nameX } = newName.getBoundingClientRect();
    gsap.to(this.clientsWrapper, { width: clientX, duration: .3 });
    gsap.to(this.namesWrapper, { width: nameX, duration: .3 });
    gsap.to(newClient, { y: 0, duration: .3 });
    gsap.to(newName, { y: 0, duration: .3 });

    this.current = next;
  }

  resize() {
    if (this.current < 0) return;

    const newClient = this.clients[this.current];
    const newName = this.names[this.current];

    const { width: clientX } = newClient.getBoundingClientRect();
    const { width: nameX } = newName.getBoundingClientRect();

    gsap.set(this.clientsWrapper, { width: clientX });
    gsap.set(this.namesWrapper, { width: nameX });
  }
}

class Viewer {
  dom;
  imgWrapper;
  overlay;
  viewers = [];
  links = [];
  imgages = [];
  easing = BezierEasing(0.69, 0.27, 0.69, 0.28);
  isShown = false;
  current = 0;
  zIndex = 0;
  blur = 0;

  _call = {
    hoverIn: () => {
      if (!this.isShown) return;

      gsap.killTweensOf(this.overlay);
      gsap.killTweensOf(this.imgWrapper);

      gsap.to(this.overlay, { height: '100%', duration: .6, ease: Power2.easeOut });
      gsap.to(this.imgWrapper, { scale: 1.3, duration: .6, ease: Power2.easeOut });

      for (let i = 0; i < this.viewers.length; i++) {
        const img = this.viewers[i].wrapper;
        gsap.to(img, { filter: `blur(${this.viewers[i].blur}px)`, scale: 1.1, duration: .5, ease: Power2.easeOut });
      }
    },
    hoverOut: () => {
      if (!this.isShown) return;

      gsap.killTweensOf(this.imgWrapper);
      gsap.killTweensOf(this.overlay);

      gsap.to(this.overlay, { height: 0, duration: .3, ease: Power2.easeOut });
      gsap.to(this.imgWrapper, { scale: 1, duration: .3, ease: Power2.easeOut });

      for (let i = 0; i < this.viewers.length; i++) {
        const img = this.viewers[i].wrapper;
        gsap.to(img, { filter: 'blur(0px)', scale: 1, duration: .5, ease: Power2.easeOut });
      }
    }
  }

  constructor(__d, __size = 'sm') {
    this.dom = __d;
    this.wrapper = GetBy.class('wrapper', this.dom)[0];
    this.imgWrapper = GetBy.class('img', this.dom)[0];
    this.overlay = GetBy.class('overlay', this.dom)[0];

    this.links = GetBy.class('__item', this.dom);
    this.images = GetBy.selector('img', this.dom);

    gsap.set(this.overlay, { height: 0 });
    gsap.set(this.wrapper, { height: 0 });
    gsap.set(this.imgWrapper, { scale: 1.5, filter: 'blur(10px)' });

    gsap.set(this.links, { height: 0 });
    gsap.set(this.images, { scale: 1.5 });

    console.log('SIZE', __size);
    if (__size === 'sm') this.blur = 8;
    else this.blur = 25;

    this.addEventListeners();
  }

  addSiblingViewers(viewer) {
    this.viewers.push(viewer);
  }

  show(__d = 0) {
    let delay = __d;

    gsap.killTweensOf(this.wrapper);

    gsap.set(this.links[0], { height: '100%' });
    gsap.set(this.images[0], { scale: 1 });

    gsap.to(this.wrapper, {
      height: '100%',
      duration: 1.4,
      ease: Power3.easeOut,
      delay,
      onComplete: () => {
        this.isShown = true;
      }
    });

    gsap.killTweensOf(this.imgWrapper);
    gsap.to(this.imgWrapper, { scale: 1, duration: 1.8, ease: Power2.easeOut, delay });
    gsap.to(this.imgWrapper, { filter: 'blur(0px)', duration: .6, ease: Power3.easeOut, delay });
  }

  hide(__d = 0) {
    let delay = __d;

    this.wrapper.style.top = 0;
    this.wrapper.style.bottom = 'auto';

    gsap.killTweensOf(this.wrapper);
    gsap.to(this.wrapper, {
      height: 0,
      duration: .6,
      ease: Power3.easeOut,
      delay: delay,
      onComplete: () => {
        this.isShown = false;
      }
    });

    gsap.killTweensOf(this.imgWrapper);
    gsap.to(this.imgWrapper, { scale: 1.5, filter: 'blur(10px)', duration: .6, ease: Power2.easeOut });
  }

  loop(c) {
    const speed = Maths.normalize(120, 0, Math.abs(InfiniteScroll.speed));
    const scale = Maths.clamp(Maths.lerp(1, 1.05, speed), 1, 1.2);
    gsap.set(this.imgWrapper, { scale });
    gsap.set(this.dom, { scale });

    if (this.current !== c) {
      const oldImg = this.images[this.current];
      gsap.killTweensOf(oldImg);
      gsap.to(oldImg, { scale: 1.5, duration: 1.4, ease: Power2.easeOut });

      const newLink = this.links[c];
      const newImg = this.images[c];

      this.zIndex++;
      newLink.style.zIndex = this.zIndex;

      gsap.killTweensOf(newLink);
      gsap.killTweensOf(newImg);

      gsap.set(newLink, { height: 0 });
      gsap.set(newImg, { scale: 1.5 });
      gsap.to(newLink, {
        height: '100%',
        duration: 1.4,
        ease: Power3.easeOut
      });
      gsap.to(newImg, { scale: 1, duration: 1.8, ease: Power2.easeOut });

      this.current = c;
    }
  }

  addEventListeners() {
    if (!isMobile) {
      this.dom.addEventListener(Basics.mouseOver, this._call.hoverIn);
      this.dom.addEventListener(Basics.mouseOut, this._call.hoverOut);
    }
  }

  removeEventListeners() {
    if (!isMobile) {
      this.dom.removeEventListener(Basics.mouseOver, this._call.hoverIn);
      this.dom.removeEventListener(Basics.mouseOut, this._call.hoverOut);
    }
  }
  dispose() {
    this.removeEventListeners();
  }
}

export class ScrollMachine {
  dom;
  holder;
  indexes;
  current = 0;
  threshold = 10;
  offset = 0;

  constructor(__d) {
    this.dom = __d;
    this.holder = GetBy.class('__holder', this.dom)[0];
    this.indexes = GetBy.class('__index', this.dom);
    gsap.set(this.indexes, { opacity: 0 });
  }

  show() {
    this.next();
  }

  hide() {
    const start = this.offset * this.threshold;
    const end = Math.min(start + this.threshold, this.indexes.length);

    let delay = 0;
    for (let index = 0; index < this.indexes.length; index++) {
      if (index >= start && index < end) {
        gsap.set(this.indexes[index], { opacity: 0, delay: delay * .08 });
        delay++;
      }
    }
  }

  activate(item) {
    item.classList.add('--active');

    const inner = GetBy.selector('span', item)[0];
    gsap.killTweensOf(inner);
    gsap.set(inner, { opacity: 0 });
    gsap.set(inner, { opacity: 1, delay: .1 });
    gsap.set(inner, { opacity: 0, delay: .2 });
    gsap.set(inner, { opacity: 1, delay: .3 });
  }

  deactivate(item) {
    item.classList.remove('--active');

    const inner = GetBy.selector('span', item)[0];
    gsap.killTweensOf(inner);
    gsap.set(inner, { opacity: 1 });
  }

  loop(c) {
    if (this.current !== c) {
      // Si canviem de bloc, movem el holder
      const newOffset = Math.floor(c / this.threshold);
      if (newOffset !== this.offset) {
        this.offset = newOffset;
        this.next();
      }

      // Activar / desactivar visualment
      this.deactivate(this.indexes[this.current]);
      this.activate(this.indexes[c]);
      this.indexes[c].classList.add('--active');
      this.current = c;
    }
  }

  next() {
    gsap.set(this.holder, { yPercent: this.offset * -100 });

    const start = this.offset * this.threshold;
    const end = Math.min(start + this.threshold, this.indexes.length);

    let delay = .1;
    for (let index = 0; index < this.indexes.length; index++) {
      if (index >= start && index < end) {
        gsap.set(this.indexes[index], { opacity: 1, delay: delay * .1 });
        delay++;
      } else {
        gsap.set(this.indexes[index], { opacity: 0 });
      }
    }
  }

  dispose() { }
}
